import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPostStatus,
  formatExpirationDate,
  POST_STATUS,
  POST_STATUS_TONES,
} from '../../utils/postLifecycle';

const TONE_CLASSES = {
  [POST_STATUS_TONES.GREEN]: 'bg-green-100 text-green-800 ring-green-200',
  [POST_STATUS_TONES.AMBER]: 'bg-amber-100 text-amber-800 ring-amber-200',
  [POST_STATUS_TONES.ORANGE]: 'bg-orange-100 text-orange-800 ring-orange-200',
  [POST_STATUS_TONES.RED]: 'bg-red-100 text-red-800 ring-red-200',
  [POST_STATUS_TONES.GRAY]: 'bg-gray-100 text-gray-700 ring-gray-200',
};

function getDefaultLabel(t, { status, expiresAt, locale }) {
  if (status === POST_STATUS.EXPIRED) {
    return t('post_status_expired', 'Expired');
  }
  if (status === POST_STATUS.UNKNOWN) {
    return t('post_status_unknown', 'No expiration set');
  }

  return t('post_status_expires_on', 'Expires {{date}}', {
    date: formatExpirationDate(expiresAt, locale),
  });
}

const PostStatusBadge = ({
  post,
  status: statusProp,
  tone: toneProp,
  label: labelProp,
  className = '',
}) => {
  const { t, i18n } = useTranslation();

  let status = statusProp;
  let tone = toneProp;
  let label = labelProp;
  let expiresAt;

  if (post && (status == null || tone == null || label == null)) {
    const computed = getPostStatus(post);
    status = status ?? computed.status;
    tone = tone ?? computed.tone;
    expiresAt = computed.expiresAt;
  }

  if (label == null) {
    label = getDefaultLabel(t, {
      status,
      expiresAt,
      locale: i18n.language,
    });
  }

  const toneClass = TONE_CLASSES[tone] || TONE_CLASSES[POST_STATUS_TONES.GRAY];

  return (
    <span
      className={[
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ring-1 ring-inset whitespace-nowrap',
        toneClass,
        className,
      ].filter(Boolean).join(' ')}
      role="status"
      aria-label={label}
    >
      <span
        className="w-1.5 h-1.5 rounded-full bg-current opacity-70"
        aria-hidden="true"
      />
      {label}
    </span>
  );
};

export default memo(PostStatusBadge);
